/**
 * @typedef {Record<string, Record<string, boolean>>} SelectedRowsState
 */

/**
 * Reducer function for the selected rows.
 * @param {SelectedRowsState} state - The current state.
 * @param {object} action - The action to perform.
 * @param {string} action.type - The type of the action.
 * @param {object} action.payload - The payload of the action.
 * @param {string} [action.payload.id] - The id of the row.
 * @param {string} [action.payload.groupId] - The id of the group.
 * @param {Array<TableRowCustom>} [action.payload.rows] - Rows for SELECT_GROUP_ROWS.
 * @param {Array<{ groupId: string, id: string }>} [action.payload.rangeRowRefs] - SELECT_ROWS_RANGE.
 * @param {Array<TableGroupsCustom>} [action.payload.groups] - The groups.
 * @param {SelectedRowsState} [action.payload.newState] - The new state (for RESTORE_SELECTION).
 * @returns {SelectedRowsState} - The new state.
 */
function selectedRowsReducer(state, action) {
  /** @type {SelectedRowsState} */
  const initialState = {};

  switch (action.type) {
    case 'SELECT_ROW': {
      const prevState = { ...state };
      const newState = {
        ...prevState,
        [action.payload.groupId]: {
          ...prevState[action.payload.groupId],
          [action.payload.id]: true,
        },
      };
      return newState;
    }
    case 'DESELECT_ROW': {
      const prevState = { ...state };
      const groupRows = prevState[action.payload.groupId] || {};
      const { [action.payload.id]: deletedRow, ...remainingRows } = groupRows;

      const newState = {
        ...prevState,
        [action.payload.groupId]: remainingRows,
      };
      return newState;
    }
    case 'SELECT_ROWS_RANGE': {
      const prevState = { ...state };
      /** @type {SelectedRowsState} */
      let newState = prevState;
      const range = /** @type {Array<{ groupId: string, id: string }> | undefined} */ (action.payload.rangeRowRefs);
      (range || []).forEach(({ groupId, id }) => {
        newState = {
          ...newState,
          [groupId]: {
            ...(newState[groupId] || {}),
            [id]: true,
          },
        };
      });
      return newState;
    }
    case 'SELECT_GROUP_ROWS': {
      const prevState = { ...state };
      const newState = {
        ...prevState,
        [action.payload.groupId]: {
          ...prevState[action.payload.groupId],
          ...action.payload.rows.reduce((/** @type {Record<string, boolean>} */ acc, row) => {
            acc[row.id] = true;
            return acc;
          }, /** @type {Record<string, boolean>} */ ({})),
        },
      };
      return newState;
    }
    case 'DESELECT_GROUP_ROWS': {
      const prevState = { ...state };
      const newState = {
        ...prevState,
        [action.payload.groupId]: {},
      };
      return newState;
    }
    case 'SELECT_ALL_ROWS': {
      const prevState = { ...state };
      const newState = {
        ...prevState,
        ...action.payload.groups.reduce((acc, group) => {
          group.data.forEach((/** @type {TableRowCustom} */ row) => {
            acc[group.id] = {
              ...acc[group.id],
              [row.id]: true,
            };
          });
          return acc;
        }, /** @type {Record<string, Record<string, boolean>>} */ ({})),
      };
      return newState;
    }
    case 'DESELECT_ALL_ROWS': {
      return initialState;
    }
    // Used to reselect rows after batch processing (beacause it can move groups)
    case 'RESTORE_SELECTION': {
      return action.payload.newState ?? initialState;
    }
    default:
      return state;
  }
}

export default selectedRowsReducer;
